/**
 * travelTimeService.js
 * Looks up real travel times between two locations.
 *
 * Priority order:
 *  1. Backend proxy  → /api/travel/distance  (keeps API key off the browser)
 *  2. Google Maps JS API loaded dynamically   (if key available)
 *  3. null                                    (caller shows manual mode picker)
 */

import { api } from '../api/client';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MODE_EMOJI = {
  walking: '🚶',
  taxi:    '🚕',
  driving: '🚗',
  train:   '🚂',
  flight:  '✈️',
  bus:     '🚌',
};

export const MODE_LABEL = {
  walking: 'walking',
  taxi:    'taxi / Uber',
  driving: 'driving',
  train:   'train',
  flight:  'flight',
  bus:     'bus / coach',
};

// ─── Google Maps JS API loader ─────────────────────────────────────────────────
let mapsLoadPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps?.DistanceMatrixService) return Promise.resolve(true);
  if (mapsLoadPromise) return mapsLoadPromise;

  const key = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!key || key.includes('YOUR_')) return Promise.resolve(false);

  mapsLoadPromise = new Promise(resolve => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async&v=weekly`;
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return mapsLoadPromise;
}

// ─── Single-mode distance lookup via Maps JS API ───────────────────────────────
function distanceFromMapsApi(origin, destination, mode) {
  return new Promise(resolve => {
    if (!window.google?.maps?.DistanceMatrixService) return resolve(null);

    const svc = new window.google.maps.DistanceMatrixService();
    svc.getDistanceMatrix(
      {
        origins:      [origin],
        destinations: [destination],
        travelMode:   mode.toUpperCase(),
        unitSystem:   window.google.maps.UnitSystem.METRIC,
      },
      (resp, status) => {
        if (status !== 'OK') return resolve(null);
        const el = resp?.rows?.[0]?.elements?.[0];
        if (el?.status !== 'OK') return resolve(null);
        resolve({
          durationS:    el.duration.value,
          durationText: el.duration.text,
          distanceM:    el.distance.value,
          distanceText: el.distance.text,
        });
      }
    );
  });
}

// ─── Main export ───────────────────────────────────────────────────────────────
/**
 * Returns travel info between origin and destination, or null if unavailable.
 *
 * @returns {{
 *   distanceKm: number,
 *   distanceText: string,
 *   driving: { durationS, durationText, distanceM } | null,
 *   transit: { durationS, durationText, distanceM } | null,
 *   recommendedMode: string,
 *   bufferMins: number,       — suggested travel block (includes buffer)
 *   needsHotel: boolean,
 * } | null}
 */
export async function getTravelInfo(origin, destination) {
  if (!origin || !destination) return null;

  // 1. Try backend proxy
  try {
    const res = await api('/api/travel/distance', {
      method: 'POST',
      body: JSON.stringify({ origin, destination }),
    });
    if (res?.ok) {
      const data = await res.json();
      if (data?.distanceM) return enrichResult(data);
    }
  } catch { /* backend not available */ }

  // 2. Try Google Maps JS API
  const mapsLoaded = await loadGoogleMaps();
  if (mapsLoaded) {
    const [driving, transit] = await Promise.all([
      distanceFromMapsApi(origin, destination, 'DRIVING'),
      distanceFromMapsApi(origin, destination, 'TRANSIT'),
    ]);

    if (driving || transit) {
      const ref = driving || transit;
      return enrichResult({
        distanceM:    ref.distanceM,
        distanceText: ref.distanceText,
        driving,
        transit,
      });
    }
  }

  return null;
}

function enrichResult(raw) {
  const distanceKm = Math.round((raw.distanceM || 0) / 1000);
  const mode = recommendMode(distanceKm, raw.driving, raw.transit);
  const bestDurationS = Math.min(
    raw.driving?.durationS  ?? Infinity,
    raw.transit?.durationS  ?? Infinity
  );
  const travelMins = bestDurationS === Infinity ? 60 : Math.round(bestDurationS / 60);
  // Buffer = travel time + 15-min padding
  const bufferMins = travelMins + 15;

  return {
    distanceKm,
    distanceText:    raw.distanceText || `${distanceKm} km`,
    driving:         raw.driving  || null,
    transit:         raw.transit  || null,
    recommendedMode: mode,
    travelMins,
    bufferMins,
    needsHotel:      distanceKm > 150,   // > 150 km likely needs overnight stay
  };
}

function recommendMode(km, driving, transit) {
  if (km < 1.5)  return 'walking';
  if (km < 8)    return 'taxi';
  // Prefer whichever is faster in the mid range
  if (km < 80) {
    const drivingSecs = driving?.durationS ?? Infinity;
    const transitSecs = transit?.durationS ?? Infinity;
    return drivingSecs <= transitSecs ? 'driving' : 'train';
  }
  if (km < 400)  return 'train';   // UK rail covers most city pairs under 400 km
  return 'flight';
}

// ─── Booking links ─────────────────────────────────────────────────────────────
export function getBookingLinks(origin, destination, mode) {
  const o = encodeURIComponent(origin);
  const d = encodeURIComponent(destination);

  switch (mode) {
    case 'flight':
      return {
        primary:   { label: 'Search flights on Google', url: `https://www.google.com/travel/flights?q=flights+from+${o}+to+${d}` },
        secondary: { label: 'Skyscanner',               url: `https://www.skyscanner.net/transport/flights/` },
        hotels:    { label: 'Search hotels',             url: `https://www.booking.com/searchresults.html?ss=${d}` },
      };
    case 'train':
      return {
        primary:   { label: 'Book on Trainline',   url: `https://www.thetrainline.com/` },
        secondary: { label: 'National Rail',        url: `https://www.nationalrail.co.uk/` },
        hotels:    { label: 'Search hotels',        url: `https://www.booking.com/searchresults.html?ss=${d}` },
      };
    case 'driving':
      return {
        primary:   { label: 'Google Maps directions', url: `https://www.google.com/maps/dir/${o}/${d}` },
        hotels:    { label: 'Search hotels',           url: `https://www.booking.com/searchresults.html?ss=${d}` },
      };
    case 'taxi':
      return {
        primary:   { label: 'Uber',           url: `https://m.uber.com/looking` },
        secondary: { label: 'Google Maps',    url: `https://www.google.com/maps/dir/${o}/${d}` },
      };
    default:
      return {
        primary: { label: 'Plan journey', url: `https://www.google.com/maps/dir/${o}/${d}` },
      };
  }
}

// ─── Cluster check ─────────────────────────────────────────────────────────────
/**
 * Given a new in-person meeting and the user's schedule, find other in-person
 * meetings on the same day at different locations.
 * Returns an array of conflicting tasks.
 */
export function findSameDayInPersonMeetings(newTask, schedule) {
  if (!newTask.date || !newTask.location) return [];
  return schedule.filter(t =>
    t.date === newTask.date &&
    t.id !== newTask.id &&
    t.meetingMode === 'in person' &&
    t.location &&
    t.location.toLowerCase() !== newTask.location.toLowerCase()
  );
}
